import { SchoolSportSeason } from '@maxpreps/apiserviceclients/apiModels';
import { ROLE } from '@maxpreps/shared/constants/authentication';
import TeamLevel from '@maxpreps/shared/constants/teamLevel';
import { ITeamContext } from '@maxpreps/shared/contexts/TeamContext';
import { IBuildAuthorizationObject } from '@maxpreps/shared/models/IBuildAuthorizationObject';
import { NIL } from 'uuid';

const getAllSeasonId = (
  teamSeasonPickerData: SchoolSportSeason[],
  teamLevel = TeamLevel.Varsity
): string =>
  teamSeasonPickerData.find(ss => ss.level === teamLevel)?.allSeasonId ?? NIL;

const getTeamAdminConfigs = (
  teamContext: ITeamContext,
  specificTeamLevel?: TeamLevel
) => {
  const { data, teamSeasonPickerData } = teamContext;
  const { teamId, allSeasonId, level } = data;

  const accessIds: string[] = [];

  // Only check permissions for a specific team level
  if (specificTeamLevel) {
    accessIds.push(getAllSeasonId(teamSeasonPickerData, specificTeamLevel));
  } else {
    // Otherwise, always check the current team level
    accessIds.push(allSeasonId);
    // Also let varsity coaches access JV and Frosh teams
    if (level !== TeamLevel.Varsity) {
      accessIds.push(getAllSeasonId(teamSeasonPickerData));
    }
  }

  return accessIds.map(accessId2 => ({
    roleId: ROLE.teamAdministrator,
    accessId1: teamId,
    accessId2
  }));
};

// NOTE: teamContext expects server format
const buildTeamAuthorizationObject: IBuildAuthorizationObject = (
  nextContext,
  preProcessedResults,
  specificTeamLevel?: TeamLevel
) => {
  const { teamContext } = preProcessedResults;

  // using this order as the most likely to be accessing this page to the least likely
  const requires = [
    ...getTeamAdminConfigs(teamContext, specificTeamLevel),
    {
      roleId: ROLE.athleticDirector,
      accessId1: teamContext?.teamId
    },
    { roleId: ROLE.standardAdmin },
    { roleId: ROLE.photographer },
    { roleId: ROLE.writer },
    {
      roleId: ROLE.associationAdministrator
      // AccessId1 check should not be performed here using schoolAssociationId
      // since a school can be associated to multiple associations
      // School-association accessibility will be checked by the APIs
      // Thus commenting line below
      // accessId1: teamContext.data.schoolAssociationId
    }
  ];

  return requires;
};

export default buildTeamAuthorizationObject;
